import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTimezoneForProvince } from '../data/CanadianLocations';
import { getTimezoneForState } from '../data/UnitedStatesLocations';

type LocationContextType = {
    city: string | null;
    province: string | null;
    /** IANA timezone derived from the selected province/state, e.g. 'America/Toronto'. */
    timezone: string | null;
    setLocation: (city: string, province: string) => void;
    clearLocation: () => void;
    isInitialized: boolean;
};

const LocationContext = createContext<LocationContextType>({
    city: null,
    province: null,
    timezone: null,
    setLocation: () => {},
    clearLocation: () => {},
    isInitialized: false,
});

/** Derives the IANA timezone for a province/state name, checking Canadian provinces first then US states. */
function deriveTimezone(province: string): string {
    // Try Canadian province first, then US state, using the same name
    const canadaTz = getTimezoneForProvince(province);
    if (canadaTz !== 'America/Toronto' || province === 'Ontario' || province === 'ON' || province === 'Quebec' || province === 'QC') {
        return canadaTz;
    }
    return getTimezoneForState(province);
}

export const LocationProvider = ({ children }: { children: React.ReactNode }) => {
    const [location, setLocation] = useState<{
        city: string | null;
        province: string | null;
        timezone: string | null;
    }>({
        city: null,
        province: null,
        timezone: null,
    });
    const [isInitialized, setIsInitialized] = useState(false); // Track initialization

    const handleSetLocation = useCallback((city: string, province: string) => {
        const tz = deriveTimezone(province);
        localStorage.setItem('userCity', city);
        localStorage.setItem('userProvince', province);
        localStorage.setItem('userTimezone', tz);
        setLocation({ city, province, timezone: tz });
    }, []);

    const handleClearLocation = useCallback(() => {
        localStorage.removeItem('userCity');
        localStorage.removeItem('userProvince');
        localStorage.removeItem('userTimezone');
        setLocation({ city: null, province: null, timezone: null });
    }, []);

    useEffect(() => {
        const savedCity = localStorage.getItem('userCity');
        const savedProvince = localStorage.getItem('userProvince');
        if (savedCity && savedProvince) {
            // Re-derive timezone in case the map was updated since last save
            const tz = deriveTimezone(savedProvince);
            localStorage.setItem('userTimezone', tz);
            setLocation({ city: savedCity, province: savedProvince, timezone: tz });
        }
        setIsInitialized(true); // Mark as initialized after checking localStorage
    }, []);

    return (
        <LocationContext.Provider
            value={{
                city: location.city,
                province: location.province,
                timezone: location.timezone,
                setLocation: handleSetLocation,
                clearLocation: handleClearLocation,
                isInitialized
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};

export const useLocation = () => useContext(LocationContext);